import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, Calendar, Building2, RotateCcw, Clock, User, Phone, Mail,
  CheckCircle, XCircle, Wrench, Sparkles, ChevronLeft, ChevronRight,
  Plus, Filter, Search, Package, Download, Edit, Play, Square, Users,
  Star, History, FileSpreadsheet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Room Status Colors (Same as therapist view - Dark variants)
const roomStatusColors = {
  maintenance: 'bg-gray-500',      // Grey - Cannot book
  available: 'bg-green-700',       // Dark Green - Can book
  booked: 'bg-red-700',            // Dark Red - Reserved
  cleaning: 'bg-blue-700',         // Dark Blue - Temporarily unavailable
};

const roomStatusLabels = {
  maintenance: 'Under Maintenance',
  available: 'Available',
  booked: 'Booked',
  cleaning: 'Cleaning',
};

// Therapy types with TBD option
const therapyTypes = [
  { name: 'TBD - Decide on arrival', duration: 60 },
  { name: 'Head, Neck & Shoulder Therapy', duration: 30 },
  { name: 'Foot Reflexology', duration: 30 },
  { name: 'Champi Head Massage', duration: 30 },
  { name: 'Swedish Massage', duration: 60 },
  { name: 'Aromatherapy Massage', duration: 60 },
  { name: 'Deep Tissue Massage', duration: 60 },
  { name: 'Balinese Massage', duration: 60 },
  { name: 'Shirodhara Therapy', duration: 60 },
  { name: 'Royal Hot Stone Therapy', duration: 90 },
  { name: 'Signature Abhyanga Ritual', duration: 90 },
  { name: 'Couples Harmony Massage', duration: 90 },
  { name: 'Custom Massage', duration: 60 }
];

const OperationsDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [futureBookings, setFutureBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activePackages, setActivePackages] = useState([]);
  const [allPackages, setAllPackages] = useState([]); // All packages including completed for history
  const [completedServices, setCompletedServices] = useState([]); // Services history
  const [propertySettings, setPropertySettings] = useState({});
  const [propertyTherapies, setPropertyTherapies] = useState([]); // Property-specific therapies
  
  const isAdmin = user?.role === 'super_admin';
  const isTherapist = user?.role === 'therapist';
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [serviceHistoryDate, setServiceHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Rate Customer Dialog
  const [rateCustomerDialogOpen, setRateCustomerDialogOpen] = useState(false);
  const [selectedServiceForRating, setSelectedServiceForRating] = useState(null);
  const [ratingForm, setRatingForm] = useState({
    overall_rating: 0,
    behavior_rating: 0,
    punctuality_rating: 0,
    hygiene_rating: 0,
    would_serve_again: null,
    comment: ''
  });
  
  // Booking Dialog
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingType, setBookingType] = useState('one_time');
  const [searchPhone, setSearchPhone] = useState('');
  const [foundPackages, setFoundPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  const [bookingForm, setBookingForm] = useState({
    guest_name: '',
    guest_gender: '',
    guest_phone: '',
    guest_email: '',
    guest_type: 'outside', // in_house or outside
    therapist_id: 'as_per_availability',
    therapy_type: '',
    therapy_duration: 60,
    base_price: '',
    discount_percent: '0',
    discount_reason: '',
    payment_type: 'postpaid', // prepaid or postpaid
    payment_mode: '',
    payment_received_by: '',
    notes: ''
  });

  // Reschedule Dialog
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    new_date: '',
    new_time: ''
  });

  // Booking Details Dialog
  const [bookingDetailsOpen, setBookingDetailsOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    payment_status: 'paid',
    payment_mode: '',
    payment_received_by: ''
  });

  // Record New Service Dialog (Therapist Flow)
  const [recordServiceOpen, setRecordServiceOpen] = useState(false);
  const [serviceFlowStep, setServiceFlowStep] = useState('select'); // select, pre_booked, package_walkin, new_walkin
  const [preBookedCustomers, setPreBookedCustomers] = useState([]);

  // Package Creation Dialog - Multi-step
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [packageStep, setPackageStep] = useState(1); // 1, 2, 3
  const [packageForm, setPackageForm] = useState({
    // Step 1 - Package Details
    package_name: '',
    guest_name: '',
    guest_gender: '',
    guest_phone: '',
    guest_email: '',
    therapy_type: 'TBD - Decide on arrival',
    total_sessions: '',
    session_duration: '60',
    total_price: '',
    validity_days: '90',
    // Step 2 - Booking Details
    guest_type: 'outside',
    therapist_id: 'as_per_availability',
    discount_percent: '0',
    payment_received_by: '',
    payment_mode: ''
  });

  // Maintenance Dialog
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [maintenanceRoom, setMaintenanceRoom] = useState(null);
  const [maintenanceForm, setMaintenanceForm] = useState({
    reason: '',
    expected_end_time: ''
  });

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProperty && selectedDate) {
      fetchBookings();
      fetchRooms();
      fetchFutureBookings();
      fetchActivePackages();
      fetchAllPackages();
      fetchPropertyTherapies();
    }
  }, [selectedProperty, selectedDate]);

  useEffect(() => {
    if (selectedProperty && serviceHistoryDate) {
      fetchCompletedServices();
    }
  }, [selectedProperty, serviceHistoryDate]);

  // Fetch property-specific therapies
  const fetchPropertyTherapies = async () => {
    if (!selectedProperty) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/property-management/therapies/${encodeURIComponent(selectedProperty)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPropertyTherapies(response.data || []);
    } catch (error) {
      console.log('Could not fetch property therapies, using defaults');
      setPropertyTherapies([]);
    }
  };

  // Combined therapy list: property-specific + fallback defaults
  const availableTherapies = useMemo(() => {
    if (propertyTherapies.length > 0) {
      // Add TBD option at the start
      return [
        { name: 'TBD - Decide on arrival', duration: 60, base_price: 0 },
        ...propertyTherapies.map(t => ({
          name: t.name,
          duration: t.duration,
          base_price: t.base_price
        }))
      ];
    }
    // Fallback to hardcoded defaults if no property therapies
    return therapyTypes;
  }, [propertyTherapies]);

  // Helper function to extract error message from API response
  const extractErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (!detail) return fallback;
    // Handle Pydantic validation errors (array of objects)
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    // Handle string errors
    if (typeof detail === 'string') return detail;
    // Handle object errors
    if (typeof detail === 'object') return detail.msg || detail.message || fallback;
    return fallback;
  };

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch properties - available to all users
      const propsRes = await axios.get(`${API}/properties`, { headers });
      const activeProps = propsRes.data.filter(p => p.status !== 'archived');
      setProperties(activeProps);
      
      // For therapists, auto-select their assigned property
      const isUserTherapist = user?.role === 'therapist';
      const assignedProperty = user?.assigned_property_id;
      
      let selectedPropName = null;
      
      if (isUserTherapist && assignedProperty) {
        // Set the assigned property
        const found = activeProps.find(p => p.hotel_name === assignedProperty);
        if (found) {
          selectedPropName = assignedProperty;
          setSelectedProperty(assignedProperty);
          // Store property settings
          setPropertySettings({
            gst_rate: found.gst_rate || 18.0,
            cleaning_buffer_minutes: found.cleaning_buffer_minutes || 15,
            slot_duration_minutes: found.slot_duration_minutes || 30,
            opening_time: found.opening_time || '09:00',
            closing_time: found.closing_time || '21:00'
          });
          // Fetch therapists for this property (may fail, that's okay)
          try {
            const therapistsRes = await axios.get(`${API}/therapists?property_id=${assignedProperty}`, { headers });
            setTherapists(therapistsRes.data.filter(t => t.status !== 'archived'));
          } catch (err) {
            console.log('Could not fetch therapists list');
            setTherapists([]);
          }
        } else {
          // Property not found - might be archived
          toast.error(`Your assigned property "${assignedProperty}" is not available. Please contact admin.`);
          if (activeProps.length > 0) {
            setSelectedProperty(activeProps[0].hotel_name);
          }
        }
      } else {
        // Admin: fetch all therapists and auto-select first property
        try {
          const therapistsRes = await axios.get(`${API}/therapists`, { headers });
          setTherapists(therapistsRes.data.filter(t => t.status !== 'archived'));
        } catch (err) {
          console.log('Could not fetch therapists list');
          setTherapists([]);
        }
        
        if (activeProps.length > 0 && !selectedProperty) {
          selectedPropName = activeProps[0].hotel_name;
          setSelectedProperty(activeProps[0].hotel_name);
          const prop = activeProps[0];
          setPropertySettings({
            gst_rate: prop.gst_rate || 18.0,
            cleaning_buffer_minutes: prop.cleaning_buffer_minutes || 15,
            slot_duration_minutes: prop.slot_duration_minutes || 30,
            opening_time: prop.opening_time || '09:00',
            closing_time: prop.closing_time || '21:00'
          });
        }
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
      toast.error('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/rooms?property_id=${selectedProperty}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let roomsData = response.data;
      
      // If no rooms found, try to sync rooms for this property
      if (roomsData.length === 0 && selectedProperty) {
        const property = properties.find(p => p.hotel_name === selectedProperty);
        if (property && property.total_spa_rooms > 0) {
          try {
            // Auto-sync rooms
            const syncResponse = await axios.post(
              `${API}/properties/${encodeURIComponent(selectedProperty)}/sync-rooms`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (syncResponse.data.rooms_created > 0) {
              toast.success(`Created ${syncResponse.data.rooms_created} rooms for ${selectedProperty}`);
              // Refetch rooms after sync
              const newRoomsResponse = await axios.get(`${API}/rooms?property_id=${selectedProperty}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              roomsData = newRoomsResponse.data;
            }
          } catch (syncError) {
            console.error('Failed to sync rooms:', syncError);
          }
        }
      }
      
      setRooms(roomsData);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/bookings?property_id=${selectedProperty}&date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    }
  };

  const fetchFutureBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      // Fetch bookings for next 30 days
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 30);
      const endDate = futureDate.toISOString().split('T')[0];
      
      const response = await axios.get(
        `${API}/bookings?property_id=${selectedProperty}&date_from=${today}&date_to=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filter for future dates only (after today)
      const futureDateFiltered = response.data.filter(b => {
        const bookingDate = b.booking_date || b.date;
        return bookingDate > today;
      });
      setFutureBookings(futureDateFiltered);
    } catch (error) {
      console.error('Failed to load future bookings:', error);
      // Fallback - just show empty
      setFutureBookings([]);
    }
  };

  const fetchActivePackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/packages?property_id=${selectedProperty}&status=active`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Filter packages with remaining sessions
      const active = response.data.filter(p => p.remaining_sessions > 0);
      setActivePackages(active);
    } catch (error) {
      console.error('Failed to load active packages:', error);
      setActivePackages([]);
    }
  };

  // Fetch ALL packages for the property (active + completed) for Packages Active tab
  const fetchAllPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/packages?property_id=${selectedProperty}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllPackages(response.data || []);
    } catch (error) {
      console.error('Failed to load all packages:', error);
      setAllPackages([]);
    }
  };

  // Fetch completed services for Services History
  const fetchCompletedServices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/bookings?property_id=${selectedProperty}&date=${serviceHistoryDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Filter only completed services
      const completed = response.data.filter(b => b.status === 'completed');
      setCompletedServices(completed);
    } catch (error) {
      console.error('Failed to load completed services:', error);
      setCompletedServices([]);
    }
  };

  // Submit customer rating
  const handleSubmitRating = async () => {
    if (!selectedServiceForRating) return;
    
    if (!ratingForm.overall_rating || !ratingForm.behavior_rating || 
        !ratingForm.punctuality_rating || !ratingForm.hygiene_rating ||
        ratingForm.would_serve_again === null) {
      toast.error('Please fill all rating fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/customer-ratings`, {
        booking_id: selectedServiceForRating.id,
        customer_phone: selectedServiceForRating.guest_phone,
        customer_name: selectedServiceForRating.guest_name,
        property_id: selectedProperty,
        overall_rating: ratingForm.overall_rating,
        behavior_rating: ratingForm.behavior_rating,
        punctuality_rating: ratingForm.punctuality_rating,
        hygiene_rating: ratingForm.hygiene_rating,
        would_serve_again: ratingForm.would_serve_again,
        comment: ratingForm.comment || null
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Customer rating submitted!');
      setRateCustomerDialogOpen(false);
      setSelectedServiceForRating(null);
      setRatingForm({
        overall_rating: 0,
        behavior_rating: 0,
        punctuality_rating: 0,
        hygiene_rating: 0,
        would_serve_again: null,
        comment: ''
      });
      fetchCompletedServices();
    } catch (error) {
      console.error('Rating error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    }
  };

  // Export packages to Excel
  const exportPackagesToExcel = () => {
    const data = allPackages.map(pkg => ({
      'Package Name': pkg.package_name,
      'Guest Name': pkg.guest_name,
      'Phone': pkg.guest_phone,
      'Email': pkg.guest_email || '-',
      'Therapy Type': pkg.therapy_type,
      'Total Sessions': pkg.total_sessions,
      'Used Sessions': pkg.used_sessions || 0,
      'Remaining': pkg.remaining_sessions || (pkg.total_sessions - (pkg.used_sessions || 0)),
      'Status': pkg.status,
      'Total Price': pkg.total_price,
      'Valid Until': pkg.valid_until,
      'Created At': pkg.created_at ? new Date(pkg.created_at).toLocaleDateString() : '-'
    }));

    // Create CSV
    const headers = Object.keys(data[0] || {});
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `packages_${selectedProperty}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Packages exported to Excel');
  };

  const currentProperty = useMemo(() => {
    return properties.find(p => p.hotel_name === selectedProperty);
  }, [properties, selectedProperty]);

  // Generate time slots based on property hours
  const timeSlots = useMemo(() => {
    if (!currentProperty) return [];
    
    const openingTime = currentProperty.opening_time || '09:00';
    const closingTime = currentProperty.closing_time || '21:00';
    
    const [openHour, openMin] = openingTime.split(':').map(Number);
    const [closeHour, closeMin] = closingTime.split(':').map(Number);
    
    const slots = [];
    let currentHour = openHour;
    let currentMin = openMin;
    
    while (currentHour < closeHour || (currentHour === closeHour && currentMin < closeMin)) {
      slots.push(`${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`);
      currentMin += 30;
      if (currentMin >= 60) {
        currentMin = 0;
        currentHour++;
      }
    }
    
    return slots;
  }, [currentProperty]);

  // Get slot status for a room at a given time
  const getSlotStatus = (roomId, time) => {
    const room = rooms.find(r => r.id === roomId);
    
    if (room?.status === 'maintenance') {
      return { status: 'maintenance', booking: null };
    }
    if (room?.status === 'cleaning') {
      return { status: 'cleaning', booking: null };
    }
    
    // Check if there's a booking overlapping this slot
    const slotBooking = bookings.find(b => {
      if (b.room_id !== roomId) return false;
      const bookingStart = b.start_time;
      const bookingEnd = b.end_time;
      return time >= bookingStart && time < bookingEnd;
    });
    
    if (slotBooking) {
      return { status: 'booked', booking: slotBooking };
    }
    
    return { status: 'available', booking: null };
  };

  const handleSlotClick = (room, time, slotInfo) => {
    if (slotInfo.status === 'available') {
      setSelectedSlot({ room, time });
      setBookingDialogOpen(true);
      resetBookingForm();
    } else if (slotInfo.status === 'booked' && slotInfo.booking) {
      // Show booking details dialog
      setSelectedBooking(slotInfo.booking);
      setBookingDetailsOpen(true);
    } else if (slotInfo.status === 'cleaning') {
      // Mark room as available
      handleMarkRoomAvailable(room.id);
    }
  };

  const handleMarkRoomAvailable = async (roomId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/rooms/${roomId}/status`, 
        { status: 'available' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Room marked as available');
      fetchRooms();
    } catch (error) {
      toast.error('Failed to update room status');
    }
  };

  const handleStartMaintenance = (room) => {
    setMaintenanceRoom(room);
    setMaintenanceForm({ reason: '', expected_end_time: '' });
    setMaintenanceDialogOpen(true);
  };

  const submitMaintenance = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/maintenance`, {
        property_id: selectedProperty,
        room_id: maintenanceRoom.id,
        reason: maintenanceForm.reason,
        expected_end_time: maintenanceForm.expected_end_time
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Room marked under maintenance');
      setMaintenanceDialogOpen(false);
      fetchRooms();
    } catch (error) {
      toast.error('Failed to start maintenance');
    }
  };

  const handleCompleteMaintenance = async (room) => {
    if (!confirm(`Mark ${room.room_name} as available? This will end the maintenance period.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/rooms/${room.id}/status`, 
        { status: 'available' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Maintenance completed - room is now available');
      fetchRooms();
    } catch (error) {
      toast.error('Failed to complete maintenance');
    }
  };

  const handleExtendMaintenance = (room) => {
    setMaintenanceRoom(room);
    setMaintenanceForm({ 
      reason: room.maintenance_reason || '', 
      expected_end_time: room.maintenance_end_time || '' 
    });
    setMaintenanceDialogOpen(true);
  };

  const handleCheckout = async () => {
    if (!selectedBooking) return;
    
    try {
      const token = localStorage.getItem('token');
      const isOwnedProperty = currentProperty?.ownership_type === 'our_property' || currentProperty?.ownership_type === 'owned_with_rent';
      
      await axios.post(`${API}/bookings/${selectedBooking.id}/checkout`, {
        payment_status: checkoutForm.payment_status,
        payment_mode: checkoutForm.payment_mode,
        payment_received_by: isOwnedProperty ? 'nirvaana' : checkoutForm.payment_received_by
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Booking checked out successfully!');
      setCheckoutDialogOpen(false);
      setBookingDetailsOpen(false);
      fetchBookings();
      fetchRooms();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Checkout failed');
      toast.error(errorMsg);
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    
    if (!confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bookings/${selectedBooking.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Booking cancelled');
      setBookingDetailsOpen(false);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  // Booking lifecycle handlers
  const handleCheckIn = async (bookingToUse) => {
    const booking = bookingToUse || selectedBooking;
    if (!booking) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bookings/${booking.id}/check-in`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${booking.guest_name} checked in!`);
      setBookingDetailsOpen(false);
      fetchBookings();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to check in');
      toast.error(errorMsg);
    }
  };

  const handleStartTherapy = async (bookingToUse) => {
    const booking = bookingToUse || selectedBooking;
    if (!booking) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bookings/${booking.id}/start-therapy`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Therapy session started');
      setBookingDetailsOpen(false);
      fetchBookings();
      fetchRooms();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to start therapy');
      toast.error(errorMsg);
    }
  };

  const handleCompleteTherapy = async (bookingToUse) => {
    const booking = bookingToUse || selectedBooking;
    if (!booking) return;
    
    try {
      const token = localStorage.getItem('token');
      const isOwnedProperty = currentProperty?.ownership_type === 'our_property' || currentProperty?.ownership_type === 'owned_with_rent';
      
      // For package bookings, complete directly
      if (booking.booking_type === 'package') {
        await axios.post(`${API}/bookings/${booking.id}/complete`, {
          payment_status: 'package_prepaid'
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        toast.success('Session completed! Room is now in cleaning mode.');
        setBookingDetailsOpen(false);
        fetchBookings();
        fetchRooms();
      } else {
        // For one-time bookings, open checkout dialog
        setSelectedBooking(booking);
        setCheckoutForm({
          payment_status: 'paid',
          payment_mode: '',
          payment_received_by: isOwnedProperty ? 'nirvaana' : ''
        });
        setCheckoutDialogOpen(true);
      }
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to complete therapy');
      toast.error(errorMsg);
    }
  };

  const handleMarkNoShow = async () => {
    if (!selectedBooking) return;
    
    if (!confirm('Mark this booking as No-Show? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bookings/${selectedBooking.id}/no-show`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Booking marked as No-Show');
      setBookingDetailsOpen(false);
      fetchBookings();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to mark as no-show');
      toast.error(errorMsg);
    }
  };

  // Record New Service for Therapists
  const handleOpenRecordService = async () => {
    // Fetch today's bookings for this property to show pre-booked customers
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(
        `${API}/bookings?property_id=${selectedProperty}&date=${today}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filter for scheduled bookings only
      const scheduled = response.data.filter(b => b.status === 'scheduled');
      setPreBookedCustomers(scheduled);
      setServiceFlowStep('select');
      setRecordServiceOpen(true);
    } catch (error) {
      toast.error('Failed to load bookings');
    }
  };

  const handleSelectPreBooked = (booking) => {
    setSelectedBooking(booking);
    setRecordServiceOpen(false);
    setBookingDetailsOpen(true);
  };

  const handleCreatePackage = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Calculate validity dates
      const today = new Date();
      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + parseInt(packageForm.validity_days));
      
      // Get property ownership type
      const isOwnedProperty = currentProperty?.ownership_type === 'our_property' || currentProperty?.ownership_type === 'owned_with_rent';
      
      // Calculate discount amount from percentage
      const basePrice = parseFloat(packageForm.total_price) || 0;
      const discountPercent = parseFloat(packageForm.discount_percent) || 0;
      const discountAmount = basePrice * (discountPercent / 100);
      const netPrice = basePrice - discountAmount;
      const gstAmount = netPrice * 0.05;
      const totalAmount = netPrice + gstAmount;
      
      const payload = {
        property_id: selectedProperty,
        package_name: packageForm.package_name,
        guest_name: packageForm.guest_name,
        guest_gender: packageForm.guest_gender,
        guest_phone: packageForm.guest_phone,
        guest_email: packageForm.guest_email || null,
        therapy_type: packageForm.therapy_type,
        session_duration: parseInt(packageForm.session_duration),
        total_sessions: parseInt(packageForm.total_sessions),
        base_price: basePrice,
        discount_percent: discountPercent,
        discount_amount: discountAmount,
        gst_amount: gstAmount,
        total_price: totalAmount,
        guest_type: packageForm.guest_type,
        therapist_id: packageForm.therapist_id === 'as_per_availability' ? null : packageForm.therapist_id,
        payment_received_by: isOwnedProperty ? 'nirvaana' : packageForm.payment_received_by,
        payment_mode: packageForm.payment_mode,
        valid_from: today.toISOString().split('T')[0],
        valid_until: validUntil.toISOString().split('T')[0]
      };
      
      await axios.post(`${API}/packages`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Package created successfully!');
      setPackageDialogOpen(false);
      resetPackageForm();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to create package');
      toast.error(errorMsg);
    }
  };

  const resetBookingForm = () => {
    setBookingForm({
      guest_name: '',
      guest_gender: '',
      guest_phone: '',
      guest_email: '',
      guest_type: 'outside',
      therapist_id: 'as_per_availability',
      therapy_type: '',
      therapy_duration: 60,
      base_price: '',
      discount_percent: '0',
      discount_reason: '',
      payment_type: 'postpaid',
      payment_mode: '',
      payment_received_by: '',
      notes: ''
    });
    setBookingType('one_time');
    setSelectedPackage(null);
    setFoundPackages([]);
    setSearchPhone('');
  };

  const resetPackageForm = () => {
    setPackageForm({
      package_name: '',
      guest_name: '',
      guest_gender: '',
      guest_phone: '',
      guest_email: '',
      therapy_type: 'TBD - Decide on arrival',
      total_sessions: '',
      session_duration: '60',
      total_price: '',
      validity_days: '90',
      guest_type: 'outside',
      therapist_id: 'as_per_availability',
      discount_percent: '0',
      payment_received_by: '',
      payment_mode: ''
    });
    setPackageStep(1);
  };

  // Handle reschedule booking
  const handleOpenReschedule = (booking) => {
    setRescheduleBooking(booking);
    setRescheduleForm({
      new_date: '',
      new_time: ''
    });
    setRescheduleDialogOpen(true);
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleForm.new_date || !rescheduleForm.new_time) {
      toast.error('Please select new date and time');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/bookings/${rescheduleBooking.id}/reschedule`,
        {
          new_date: rescheduleForm.new_date,
          new_time: rescheduleForm.new_time
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Booking rescheduled successfully');
      setRescheduleDialogOpen(false);
      fetchBookings();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to reschedule');
      toast.error(errorMsg);
    }
  };

  // Export future bookings to Excel
  const exportToExcel = () => {
    if (futureBookings.length === 0) {
      toast.error('No future bookings to export');
      return;
    }

    // Create CSV content
    const headers = ['Booking Date', 'Time', 'Room', 'Guest Name', 'Phone', 'Email', 'Therapy', 'Duration', 'Type', 'Status', 'Base Price', 'Discount', 'GST', 'Total', 'Therapist', 'Payment Status', 'Payment Mode', 'Created At'];
    
    const rows = futureBookings.map(b => {
      const room = rooms.find(r => r.id === b.room_id);
      const therapist = therapists.find(t => t.user_id === b.therapist_id);
      return [
        b.booking_date,
        `${b.start_time} - ${b.end_time}`,
        room?.room_name || 'N/A',
        b.guest_name,
        b.guest_phone || '',
        b.guest_email || '',
        b.therapy_type,
        `${b.therapy_duration} mins`,
        b.booking_type,
        b.status,
        b.base_price || 0,
        b.discount_amount || 0,
        b.gst_amount || 0,
        b.total_amount || 0,
        therapist?.name || 'N/A',
        b.payment_status,
        b.payment_mode || '',
        b.created_at
      ].map(v => `"${v}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `future_bookings_${selectedProperty}_${selectedDate}.csv`;
    link.click();
    
    toast.success('Bookings exported successfully');
  };

  const searchPackagesByPhone = async () => {
    if (!searchPhone || searchPhone.length < 10) {
      toast.error('Enter valid phone number');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/packages/search?phone=${searchPhone}&property_id=${selectedProperty}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFoundPackages(response.data);
      
      if (response.data.length === 0) {
        toast.info('No active packages found for this phone');
      }
    } catch (error) {
      toast.error('Failed to search packages');
    }
  };

  const selectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setBookingForm({
      ...bookingForm,
      guest_name: pkg.guest_name,
      guest_gender: pkg.guest_gender || '',
      guest_phone: pkg.guest_phone,
      guest_email: pkg.guest_email || '',
      therapy_type: pkg.therapy_type,
      base_price: '0',
      discount_percent: '0',
      payment_type: 'prepaid' // Package is always prepaid
    });
  };

  const handleTherapyChange = (therapyName) => {
    const therapy = availableTherapies.find(t => t.name === therapyName);
    setBookingForm({
      ...bookingForm,
      therapy_type: therapyName,
      therapy_duration: therapy?.duration || 60,
      // Auto-fill base price if available from property therapies
      base_price: therapy?.base_price > 0 ? therapy.base_price.toString() : bookingForm.base_price
    });
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    
    if (!selectedSlot) return;
    
    // For package booking, require a selected package
    if (bookingType === 'package' && !selectedPackage) {
      toast.error('Please search and select a package first');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Get property ownership type
      const isOwnedProperty = currentProperty?.ownership_type === 'our_property' || currentProperty?.ownership_type === 'owned_with_rent';
      
      // Calculate discount amount from percentage
      const basePrice = parseFloat(bookingForm.base_price) || 0;
      const discountPercent = parseFloat(bookingForm.discount_percent) || 0;
      const discountAmount = basePrice * (discountPercent / 100);
      
      const payload = {
        property_id: selectedProperty,
        room_id: selectedSlot.room.id,
        guest_name: bookingForm.guest_name,
        guest_gender: bookingForm.guest_gender || null,
        guest_phone: bookingForm.guest_phone,
        guest_email: bookingForm.guest_email || null,
        booking_type: bookingType,
        package_id: selectedPackage?.id || null,
        booking_date: selectedDate,
        start_time: selectedSlot.time,
        therapy_type: bookingForm.therapy_type,
        therapy_duration: parseInt(bookingForm.therapy_duration),
        guest_type: bookingForm.guest_type,
        therapist_id: bookingForm.therapist_id === 'as_per_availability' ? null : (bookingForm.therapist_id || null),
        base_price: basePrice,
        discount_amount: discountAmount,
        discount_percent: discountPercent,
        discount_reason: bookingForm.discount_reason || null,
        payment_type: bookingForm.payment_type,
        payment_status: bookingForm.payment_type === 'prepaid' ? 'paid' : 'pending',
        payment_mode: bookingForm.payment_type === 'prepaid' ? bookingForm.payment_mode : null,
        payment_received_by: bookingForm.payment_type === 'prepaid' ? (isOwnedProperty ? 'nirvaana' : bookingForm.payment_received_by) : null,
        notes: bookingForm.notes || null
      };
      
      await axios.post(`${API}/bookings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Booking created successfully!');
      setBookingDialogOpen(false);
      resetBookingForm();
      fetchBookings();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to create booking');
      toast.error(errorMsg);
    }
  };

  const changeDate = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  const resetFilters = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  // Calculate final price using 5% GST (discount is percentage-based)
  const finalPrice = useMemo(() => {
    const base = parseFloat(bookingForm.base_price) || 0;
    const discountPercent = parseFloat(bookingForm.discount_percent) || 0;
    const discountAmount = base * (discountPercent / 100);
    const net = base - discountAmount;
    const gstRate = 0.05; // Fixed 5% GST
    const gst = net * gstRate;
    return {
      base,
      discountPercent,
      discountAmount: discountAmount.toFixed(2),
      net,
      gst: gst.toFixed(2),
      gstRate: 5,
      total: (net + gst).toFixed(2)
    };
  }, [bookingForm.base_price, bookingForm.discount_percent]);

  // Calculate package price with GST
  const packagePrice = useMemo(() => {
    const base = parseFloat(packageForm.total_price) || 0;
    const discountPercent = parseFloat(packageForm.discount_percent) || 0;
    const discountAmount = base * (discountPercent / 100);
    const net = base - discountAmount;
    const gst = net * 0.05;
    return {
      base,
      discountPercent,
      discountAmount: discountAmount.toFixed(2),
      net,
      gst: gst.toFixed(2),
      total: (net + gst).toFixed(2)
    };
  }, [packageForm.total_price, packageForm.discount_percent]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={user?.role === 'therapist' ? '/therapist' : '/admin'}>
              <Button variant="outline" size="sm" data-testid="back-button">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </Link>
            <h1 className="text-2xl font-serif text-foreground">Operations Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Therapist: Record New Service button */}
            {isTherapist && (
              <Button 
                onClick={handleOpenRecordService} 
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                data-testid="record-service-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Record New Service
              </Button>
            )}
            {user?.role === 'super_admin' && (
              <Button variant="outline" size="sm" onClick={() => setPackageDialogOpen(true)} data-testid="create-package-btn">
                <Plus className="w-4 h-4 mr-1" /> New Package
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onLogout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Filters */}
        <div className="glass rounded-2xl p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label>Property</Label>
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger data-testid="property-select">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.hotel_name}>{p.hotel_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => changeDate(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-[160px]"
                data-testid="date-select"
              />
              <Button variant="outline" size="icon" onClick={() => changeDate(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            <Button variant="outline" onClick={resetFilters} data-testid="reset-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
          </div>
          
          {currentProperty && (
            <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {currentProperty.opening_time || '09:00'} - {currentProperty.closing_time || '21:00'}
              </span>
              <span className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {rooms.length} Room(s)
              </span>
              <Badge variant={(currentProperty.ownership_type === 'our_property' || currentProperty.ownership_type === 'owned_with_rent') ? 'default' : 'secondary'}>
                {currentProperty.ownership_type === 'our_property' ? 'Fully Owned' : currentProperty.ownership_type === 'owned_with_rent' ? 'Owned (Rent)' : 'Partner'}
              </Badge>
            </div>
          )}
        </div>

        {/* Room-wise Slot Grid */}
        <div className="glass rounded-2xl p-6 overflow-x-auto">
          <h3 className="text-lg font-serif text-foreground mb-4">Room Availability</h3>
          
          {rooms.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No rooms configured for this property.
              {isAdmin ? (
                <Link to="/admin/properties" className="text-primary ml-2 underline">Configure rooms</Link>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="ml-2"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      await axios.post(
                        `${API}/properties/${encodeURIComponent(selectedProperty)}/sync-rooms`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      toast.success('Rooms created successfully');
                      fetchRooms();
                    } catch (error) {
                      toast.error('Failed to create rooms. Contact admin.');
                    }
                  }}
                >
                  Sync Rooms
                </Button>
              )}
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-2 text-left text-sm font-medium text-muted-foreground sticky left-0 bg-background">
                    Room
                  </th>
                  {timeSlots.map(slot => (
                    <th key={slot} className="p-1 text-xs text-muted-foreground min-w-[60px]">
                      {slot}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rooms.map(room => (
                  <tr key={room.id} className="border-t border-border/30">
                    <td className="p-2 text-sm font-medium sticky left-0 bg-background">
                      <div className="flex items-center gap-2">
                        {room.room_name}
                        {room.status === 'maintenance' && (
                          <div className="flex items-center gap-1">
                            <Badge variant="destructive" className="text-[10px]">Maintenance</Badge>
                            {(isAdmin || isTherapist) && (
                              <>
                                <button 
                                  onClick={() => handleCompleteMaintenance(room)}
                                  className="p-1 hover:bg-green-100 rounded"
                                  title="Complete Maintenance"
                                >
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                </button>
                                <button 
                                  onClick={() => handleExtendMaintenance(room)}
                                  className="p-1 hover:bg-amber-100 rounded"
                                  title="Extend/Edit Maintenance"
                                >
                                  <Clock className="w-3 h-3 text-amber-600" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        {room.status === 'cleaning' && (
                          <div className="flex items-center gap-1">
                            <Badge variant="secondary" className="text-[10px]">Cleaning</Badge>
                            {(isAdmin || isTherapist) && (
                              <button 
                                onClick={() => handleMarkRoomAvailable(room.id)}
                                className="p-1 hover:bg-green-100 rounded"
                                title="Mark as Available"
                              >
                                <CheckCircle className="w-3 h-3 text-green-600" />
                              </button>
                            )}
                          </div>
                        )}
                        {room.status === 'available' && (isAdmin || isTherapist) && (
                          <button 
                            onClick={() => handleStartMaintenance(room)}
                            className="ml-1 p-1 hover:bg-muted rounded"
                            title="Start Maintenance"
                          >
                            <Wrench className="w-3 h-3 text-muted-foreground" />
                          </button>
                        )}
                      </div>
                    </td>
                    {timeSlots.map(slot => {
                      const slotInfo = getSlotStatus(room.id, slot);
                      return (
                        <td key={`${room.id}-${slot}`} className="p-1">
                          <button
                            onClick={() => handleSlotClick(room, slot, slotInfo)}
                            disabled={slotInfo.status === 'maintenance'}
                            className={`w-full h-10 rounded text-[10px] font-medium transition-all ${
                              slotInfo.status === 'available' 
                                ? 'bg-green-100 hover:bg-green-200 text-green-700 cursor-pointer'
                                : slotInfo.status === 'booked'
                                ? 'bg-primary/20 text-primary cursor-pointer'
                                : slotInfo.status === 'cleaning'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            }`}
                            data-testid={`slot-${room.id}-${slot}`}
                          >
                            {slotInfo.status === 'booked' && slotInfo.booking?.guest_name?.split(' ')[0]}
                            {slotInfo.status === 'available' && '+'}
                            {slotInfo.status === 'cleaning' && <Sparkles className="w-3 h-3 mx-auto" />}
                            {slotInfo.status === 'maintenance' && <Wrench className="w-3 h-3 mx-auto" />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {/* Legend */}
          <div className="flex items-center gap-6 mt-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-green-100"></div> Available
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-primary/20"></div> Booked
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-blue-100"></div> Cleaning
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-gray-200"></div> Maintenance
            </span>
          </div>
        </div>

        {/* Today's Bookings Table */}
        <div className="glass rounded-2xl p-6">
          <Tabs defaultValue="today">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-serif text-foreground">Operations</h3>
              <TabsList className="flex-wrap">
                <TabsTrigger value="today">Today ({bookings.filter(b => b.booking_date === selectedDate && b.status !== 'completed').length})</TabsTrigger>
                <TabsTrigger value="future">Future</TabsTrigger>
                <TabsTrigger value="packages" className="flex items-center gap-1">
                  <Package className="w-3 h-3" /> Packages Active ({allPackages.filter(p => (p.remaining_sessions || (p.total_sessions - (p.used_sessions || 0))) > 0).length})
                </TabsTrigger>
                <TabsTrigger value="history" className="flex items-center gap-1">
                  <History className="w-3 h-3" /> Services History
                </TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="today">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left py-2 px-2 text-sm text-muted-foreground">Time</th>
                      <th className="text-left py-2 px-2 text-sm text-muted-foreground">Room</th>
                      <th className="text-left py-2 px-2 text-sm text-muted-foreground">Guest</th>
                      <th className="text-left py-2 px-2 text-sm text-muted-foreground">Phone</th>
                      <th className="text-left py-2 px-2 text-sm text-muted-foreground">Therapy</th>
                      <th className="text-left py-2 px-2 text-sm text-muted-foreground">Type</th>
                      <th className="text-right py-2 px-2 text-sm text-muted-foreground">Amount</th>
                      <th className="text-left py-2 px-2 text-sm text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.filter(b => b.booking_date === selectedDate && b.status !== 'completed').map(booking => {
                      const room = rooms.find(r => r.id === booking.room_id);
                      const statusConfig = {
                        scheduled: { bg: 'bg-yellow-50', badge: 'bg-yellow-100 text-yellow-700', label: 'Scheduled' },
                        checked_in: { bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700', label: 'Checked In' },
                        in_progress: { bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700', label: 'In Progress' },
                        completed: { bg: 'bg-green-50', badge: 'bg-green-100 text-green-700', label: 'Completed' },
                        cancelled: { bg: 'bg-red-50', badge: 'bg-red-100 text-red-700', label: 'Cancelled' },
                        no_show: { bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-700', label: 'No Show' }
                      };
                      const config = statusConfig[booking.status] || statusConfig.scheduled;
                      
                      return (
                        <tr 
                          key={booking.id} 
                          className={`border-b border-border/30 hover:bg-muted/50 cursor-pointer ${config.bg}`}
                          onClick={() => {
                            setSelectedBooking(booking);
                            setBookingDetailsOpen(true);
                          }}
                          data-testid={`booking-row-${booking.id}`}
                        >
                          <td className="py-2 px-2 text-sm">{booking.start_time} - {booking.end_time}</td>
                          <td className="py-2 px-2 text-sm">{room?.room_name || 'N/A'}</td>
                          <td className="py-2 px-2 text-sm font-medium">{booking.guest_name}</td>
                          <td className="py-2 px-2 text-sm text-muted-foreground">{booking.guest_phone}</td>
                          <td className="py-2 px-2 text-sm">{booking.therapy_type}</td>
                          <td className="py-2 px-2 text-sm">
                            <Badge variant={booking.booking_type === 'package' ? 'default' : 'outline'}>
                              {booking.booking_type === 'package' 
                                ? `Session ${booking.session_number || ''}` 
                                : 'One-time'}
                            </Badge>
                          </td>
                          <td className="py-2 px-2 text-sm text-right">₹{booking.total_amount}</td>
                          <td className="py-2 px-2 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.badge}`}>
                              {config.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {bookings.filter(b => b.booking_date === selectedDate && b.status !== 'completed').length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center py-8 text-muted-foreground">
                          No active bookings for this date
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>
            
            <TabsContent value="future">
              {futureBookings.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No future bookings found for this property
                </p>
              ) : (
                <div className="space-y-3">
                  {/* Export Button */}
                  <div className="flex justify-end">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={exportToExcel}
                      data-testid="export-future-bookings"
                    >
                      <Download className="w-4 h-4 mr-2" /> Export to Excel
                    </Button>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 text-sm font-medium">Date</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Time</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Guest</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Room</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Therapy</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Therapist</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Status</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Type</th>
                          <th className="text-center py-2 px-2 text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {futureBookings
                          .sort((a, b) => new Date(a.booking_date || a.date) - new Date(b.booking_date || b.date))
                          .map((booking) => {
                          const bookingTherapist = therapists.find(t => t.id === booking.therapist_id);
                          const bookingRoom = rooms.find(r => r.id === booking.room_id);
                          const displayDate = booking.booking_date || booking.date;
                          return (
                            <tr key={booking.id} className="border-b hover:bg-muted/30">
                              <td className="py-2 px-2 text-sm font-medium">{displayDate}</td>
                              <td className="py-2 px-2 text-sm">{booking.start_time} - {booking.end_time}</td>
                              <td className="py-2 px-2 text-sm">
                                <div className="font-medium">{booking.guest_name}</div>
                                <div className="text-xs text-muted-foreground">{booking.guest_phone}</div>
                              </td>
                              <td className="py-2 px-2 text-sm">{bookingRoom?.room_name || 'Room ' + booking.room_number}</td>
                              <td className="py-2 px-2 text-sm">{booking.therapy_type}</td>
                              <td className="py-2 px-2 text-sm">{bookingTherapist?.full_name || 'Not assigned'}</td>
                              <td className="py-2 px-2 text-sm">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  booking.status === 'confirmed' || booking.status === 'scheduled' ? 'bg-green-100 text-green-700' :
                                  booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                  booking.status === 'blocked' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {booking.status || 'pending'}
                                </span>
                              </td>
                              <td className="py-2 px-2 text-sm">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  booking.booking_type === 'package' ? 'bg-purple-100 text-purple-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {booking.booking_type === 'package' ? 'Package' : 'One-time'}
                                </span>
                              </td>
                              <td className="py-2 px-2 text-sm text-center">
                                <div className="flex gap-1 justify-center">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleOpenReschedule(booking)}
                                    className="h-7 px-2"
                                    data-testid={`reschedule-btn-${booking.id}`}
                                  >
                                    <Edit className="w-3 h-3 mr-1" /> Edit
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Packages Active Tab */}
            <TabsContent value="packages">
              <div className="space-y-4">
                {/* Export Button */}
                <div className="flex justify-between items-center">
                  <p className="text-sm text-muted-foreground">
                    Showing all packages for this property - including active and completed
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={exportPackagesToExcel}
                    data-testid="export-packages-excel"
                    disabled={allPackages.length === 0}
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" /> Export to Excel
                  </Button>
                </div>

                {allPackages.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No packages found for this property</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {/* Active Packages */}
                    {allPackages.filter(p => (p.remaining_sessions || (p.total_sessions - (p.used_sessions || 0))) > 0).length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          Active Packages ({allPackages.filter(p => (p.remaining_sessions || (p.total_sessions - (p.used_sessions || 0))) > 0).length})
                        </h4>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {allPackages.filter(p => (p.remaining_sessions || (p.total_sessions - (p.used_sessions || 0))) > 0).map(pkg => {
                            const remaining = pkg.remaining_sessions || (pkg.total_sessions - (pkg.used_sessions || 0));
                            const used = pkg.used_sessions || 0;
                            return (
                              <div key={pkg.id} className="p-4 border border-green-200 bg-green-50/50 rounded-xl hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <h5 className="font-medium text-foreground">{pkg.package_name || pkg.therapy_type}</h5>
                                    <p className="text-sm text-foreground">{pkg.guest_name}</p>
                                  </div>
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    {remaining}/{pkg.total_sessions} left
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground space-y-1 mb-3">
                                  <p className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" /> {pkg.guest_phone}
                                  </p>
                                  {pkg.guest_email && (
                                    <p className="flex items-center gap-1">
                                      <Mail className="w-3 h-3" /> {pkg.guest_email}
                                    </p>
                                  )}
                                  <p>Therapy: {pkg.therapy_type}</p>
                                  <p>Valid till: {pkg.valid_until || 'N/A'}</p>
                                  <p className="font-medium text-foreground">Total: ₹{pkg.total_price || 0}</p>
                                </div>
                                <div className="pt-2 border-t border-green-200">
                                  <div className="w-full bg-green-200 rounded-full h-2">
                                    <div 
                                      className="bg-green-600 h-2 rounded-full transition-all" 
                                      style={{ width: `${(used / pkg.total_sessions) * 100}%` }}
                                    ></div>
                                  </div>
                                  <p className="text-xs text-center text-muted-foreground mt-1">
                                    {used} completed / {pkg.total_sessions} total
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Completed Packages (for historical view) */}
                    {allPackages.filter(p => (p.remaining_sessions || (p.total_sessions - (p.used_sessions || 0))) === 0).length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                          Completed Packages ({allPackages.filter(p => (p.remaining_sessions || (p.total_sessions - (p.used_sessions || 0))) === 0).length})
                        </h4>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {allPackages.filter(p => (p.remaining_sessions || (p.total_sessions - (p.used_sessions || 0))) === 0).map(pkg => (
                            <div key={pkg.id} className="p-4 border border-gray-200 bg-gray-50/50 rounded-xl opacity-75">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <h5 className="font-medium text-foreground">{pkg.package_name || pkg.therapy_type}</h5>
                                  <p className="text-sm text-foreground">{pkg.guest_name}</p>
                                </div>
                                <Badge className="bg-gray-100 text-gray-700 text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Completed
                                </Badge>
                              </div>
                              <div className="text-xs text-muted-foreground space-y-1">
                                <p className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {pkg.guest_phone}
                                </p>
                                <p>Therapy: {pkg.therapy_type}</p>
                                <p>Sessions: {pkg.total_sessions} / {pkg.total_sessions}</p>
                                <p className="font-medium text-foreground">Total: ₹{pkg.total_price || 0}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Services History Tab */}
            <TabsContent value="history">
              <div className="space-y-4">
                {/* Date Filter for History */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Date:</Label>
                    <Input
                      type="date"
                      value={serviceHistoryDate}
                      onChange={(e) => setServiceHistoryDate(e.target.value)}
                      className="w-40"
                      data-testid="history-date-filter"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setServiceHistoryDate(new Date().toISOString().split('T')[0])}
                  >
                    Today
                  </Button>
                </div>

                {completedServices.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No completed services for this date</p>
                    <p className="text-xs mt-1">Completed bookings will appear here</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 text-sm font-medium">Time</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Guest</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Phone</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Therapy</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Therapist</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Type</th>
                          <th className="text-right py-2 px-2 text-sm font-medium">Amount</th>
                          <th className="text-center py-2 px-2 text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completedServices.map(service => {
                          const serviceTherapist = therapists.find(t => t.id === service.therapist_id);
                          return (
                            <tr key={service.id} className="border-b hover:bg-green-50/50">
                              <td className="py-2 px-2 text-sm">{service.start_time} - {service.end_time}</td>
                              <td className="py-2 px-2 text-sm font-medium">{service.guest_name}</td>
                              <td className="py-2 px-2 text-sm text-muted-foreground">{service.guest_phone}</td>
                              <td className="py-2 px-2 text-sm">{service.therapy_type}</td>
                              <td className="py-2 px-2 text-sm">{serviceTherapist?.full_name || 'N/A'}</td>
                              <td className="py-2 px-2 text-sm">
                                <Badge variant={service.booking_type === 'package' ? 'default' : 'outline'} className="text-xs">
                                  {service.booking_type === 'package' ? 'Package' : 'One-time'}
                                </Badge>
                              </td>
                              <td className="py-2 px-2 text-sm text-right font-medium">₹{service.total_amount}</td>
                              <td className="py-2 px-2 text-center">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  onClick={() => {
                                    setSelectedServiceForRating(service);
                                    setRateCustomerDialogOpen(true);
                                  }}
                                  data-testid={`rate-customer-${service.id}`}
                                >
                                  <Star className="w-3 h-3 mr-1" /> Rate Now
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">New Booking</DialogTitle>
            {selectedSlot && (
              <p className="text-sm text-muted-foreground">
                {selectedSlot.room.room_name} • {selectedDate} at {selectedSlot.time}
              </p>
            )}
          </DialogHeader>
          
          <form onSubmit={handleCreateBooking} className="space-y-6">
            {/* Booking Type Selection */}
            <div className="p-4 bg-muted/30 rounded-lg">
              <Label className="text-base font-medium mb-3 block">Booking Type</Label>
              <RadioGroup
                value={bookingType}
                onValueChange={setBookingType}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="one_time" id="one_time" />
                  <Label htmlFor="one_time" className="cursor-pointer">One-Time Booking</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="package" id="package" />
                  <Label htmlFor="package" className="cursor-pointer">Package Booking</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Package Search (if package selected) */}
            {bookingType === 'package' && (
              <div className="p-4 border border-primary/20 rounded-lg space-y-4">
                <Label className="font-medium">Search Existing Package</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter guest phone number"
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                  />
                  <Button type="button" onClick={searchPackagesByPhone}>
                    <Search className="w-4 h-4 mr-2" /> Search
                  </Button>
                </div>
                
                {foundPackages.length > 0 && (
                  <div className="space-y-2">
                    {foundPackages.map(pkg => (
                      <div 
                        key={pkg.id}
                        onClick={() => selectPackage(pkg)}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedPackage?.id === pkg.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex justify-between">
                          <span className="font-medium">{pkg.guest_name}</span>
                          <Badge>{pkg.sessions_remaining} sessions left</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{pkg.package_name}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {selectedPackage && (
                  <div className="p-3 bg-green-50 rounded-lg text-sm">
                    <p className="font-medium text-green-700">
                      Session {selectedPackage.sessions_used + 1} of {selectedPackage.total_sessions}
                    </p>
                    <p className="text-green-600">
                      {selectedPackage.sessions_remaining - 1} sessions remaining after this
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Guest Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="guest_name">Guest Name *</Label>
                <Input
                  id="guest_name"
                  value={bookingForm.guest_name}
                  onChange={(e) => setBookingForm({ ...bookingForm, guest_name: e.target.value })}
                  required
                  disabled={bookingType === 'package' && selectedPackage}
                  data-testid="guest-name-input"
                />
              </div>
              <div>
                <Label htmlFor="guest_gender">Gender *</Label>
                <Select
                  value={bookingForm.guest_gender}
                  onValueChange={(value) => setBookingForm({ ...bookingForm, guest_gender: value })}
                  disabled={bookingType === 'package' && selectedPackage}
                >
                  <SelectTrigger data-testid="guest-gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="guest_phone">Guest Phone *</Label>
                <Input
                  id="guest_phone"
                  type="tel"
                  value={bookingForm.guest_phone}
                  onChange={(e) => setBookingForm({ ...bookingForm, guest_phone: e.target.value })}
                  required
                  disabled={bookingType === 'package' && selectedPackage}
                  data-testid="guest-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="guest_email">Guest Email</Label>
                <Input
                  id="guest_email"
                  type="email"
                  value={bookingForm.guest_email}
                  onChange={(e) => setBookingForm({ ...bookingForm, guest_email: e.target.value })}
                  placeholder="For feedback email"
                  data-testid="guest-email-input"
                />
              </div>
              <div>
                <Label htmlFor="guest_type">Guest Type *</Label>
                <Select
                  value={bookingForm.guest_type}
                  onValueChange={(value) => setBookingForm({ ...bookingForm, guest_type: value })}
                >
                  <SelectTrigger data-testid="guest-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_house">In-house Guest</SelectItem>
                    <SelectItem value="outside">Outside Guest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Therapist Assignment */}
            <div>
              <Label htmlFor="therapist_id">Assign Therapist</Label>
              <Select
                value={bookingForm.therapist_id}
                onValueChange={(value) => setBookingForm({ ...bookingForm, therapist_id: value })}
              >
                <SelectTrigger data-testid="therapist-select">
                  <SelectValue placeholder="Select therapist" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="as_per_availability">As per availability</SelectItem>
                  {therapists
                    .filter(t => t.assigned_property_id === selectedProperty)
                    .map(t => (
                      <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            {/* Therapy Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="therapy_type">Therapy Type *</Label>
                <Select
                  value={bookingForm.therapy_type}
                  onValueChange={handleTherapyChange}
                  disabled={bookingType === 'package' && selectedPackage}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select therapy" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTherapies.map(t => (
                      <SelectItem key={t.name} value={t.name}>
                        {t.name} ({t.duration} min){t.base_price > 0 ? ` - ₹${t.base_price}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="therapy_duration">Duration (minutes)</Label>
                <Input
                  id="therapy_duration"
                  type="number"
                  value={bookingForm.therapy_duration}
                  onChange={(e) => setBookingForm({ ...bookingForm, therapy_duration: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Pricing (hidden for package) */}
            {bookingType === 'one_time' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="base_price">Base Price (₹) * <span className="text-xs text-muted-foreground">(Excl. GST)</span></Label>
                    <Input
                      id="base_price"
                      type="number"
                      value={bookingForm.base_price}
                      onChange={(e) => setBookingForm({ ...bookingForm, base_price: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="discount_percent">Discount (%)</Label>
                    <Input
                      id="discount_percent"
                      type="number"
                      min="0"
                      max="100"
                      value={bookingForm.discount_percent}
                      onChange={(e) => setBookingForm({ ...bookingForm, discount_percent: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                </div>
                
                {bookingForm.base_price && (
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span>Base Price</span>
                      <span>₹{finalPrice.base}</span>
                    </div>
                    {parseFloat(finalPrice.discountAmount) > 0 && (
                      <div className="flex justify-between text-sm text-red-600">
                        <span>Discount ({finalPrice.discountPercent}%)</span>
                        <span>-₹{finalPrice.discountAmount}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span>GST (5%)</span>
                      <span>₹{finalPrice.gst}</span>
                    </div>
                    <div className="flex justify-between text-base font-medium border-t pt-2 mt-2">
                      <span>Total</span>
                      <span className="text-primary">₹{finalPrice.total}</span>
                    </div>
                  </div>
                )}
                
                {/* Payment Type */}
                <div className="p-4 bg-muted/30 rounded-lg">
                  <Label className="text-base font-medium mb-3 block">Payment Type *</Label>
                  <RadioGroup
                    value={bookingForm.payment_type}
                    onValueChange={(value) => setBookingForm({ ...bookingForm, payment_type: value })}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="prepaid" id="prepaid" />
                      <Label htmlFor="prepaid" className="cursor-pointer">Prepaid</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="postpaid" id="postpaid" />
                      <Label htmlFor="postpaid" className="cursor-pointer">Postpaid</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Payment Details (only for prepaid) */}
                {bookingForm.payment_type === 'prepaid' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="payment_received_by">Payment Received By *</Label>
                      {(currentProperty?.ownership_type === 'our_property' || currentProperty?.ownership_type === 'owned_with_rent') ? (
                        <div className="p-3 bg-green-50 rounded-lg text-green-700 text-sm">
                          Company ({currentProperty?.ownership_type === 'owned_with_rent' ? 'Owned with Rent' : 'Fully Owned'})
                        </div>
                      ) : (
                        <Select
                          value={bookingForm.payment_received_by}
                          onValueChange={(value) => setBookingForm({ ...bookingForm, payment_received_by: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hotel">Hotel</SelectItem>
                            <SelectItem value="nirvaana">Nirvaana Wellness</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="payment_mode">Payment Mode</Label>
                      <Select
                        value={bookingForm.payment_mode}
                        onValueChange={(value) => setBookingForm({ ...bookingForm, payment_mode: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="card">Card</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {bookingType === 'package' && selectedPackage && (
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-green-700 font-medium">Package Prepaid - No payment due</p>
              </div>
            )}

            {bookingType === 'package' && !selectedPackage && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-700 font-medium">Please search and select a package to continue</p>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                placeholder="Optional notes"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={bookingType === 'package' && !selectedPackage}
            >
              Create Booking
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Booking Details Dialog */}
      <Dialog open={bookingDetailsOpen} onOpenChange={setBookingDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Booking Details</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              {/* Guest Info */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <User className="w-5 h-5 text-primary" />
                  <span className="font-medium text-lg">{selectedBooking.guest_name}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    {selectedBooking.guest_phone}
                  </div>
                  {selectedBooking.guest_email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      {selectedBooking.guest_email}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Booking Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Date & Time</p>
                  <p className="font-medium">{selectedBooking.booking_date} • {selectedBooking.start_time} - {selectedBooking.end_time}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Therapy</p>
                  <p className="font-medium">{selectedBooking.therapy_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Duration</p>
                  <p className="font-medium">{selectedBooking.therapy_duration} minutes</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Type</p>
                  <Badge variant={selectedBooking.booking_type === 'package' ? 'default' : 'outline'}>
                    {selectedBooking.booking_type === 'package' 
                      ? `Package Session ${selectedBooking.session_number}` 
                      : 'One-Time'}
                  </Badge>
                </div>
              </div>
              
              {/* Pricing */}
              <div className="p-4 border rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Base Price</span>
                  <span>₹{selectedBooking.base_price}</span>
                </div>
                {selectedBooking.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Discount</span>
                    <span>-₹{selectedBooking.discount_amount}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span>GST ({selectedBooking.gst_rate || 18}%)</span>
                  <span>₹{selectedBooking.gst_amount}</span>
                </div>
                <div className="flex justify-between font-medium border-t pt-2 mt-2">
                  <span>Total</span>
                  <span className="text-primary">₹{selectedBooking.total_amount}</span>
                </div>
              </div>
              
              {/* Status & Lifecycle Actions */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Badge variant={
                    selectedBooking.status === 'completed' ? 'default' :
                    selectedBooking.status === 'in_progress' ? 'secondary' :
                    selectedBooking.status === 'checked_in' ? 'secondary' :
                    selectedBooking.status === 'cancelled' ? 'destructive' :
                    selectedBooking.status === 'no_show' ? 'destructive' : 'outline'
                  } className={
                    selectedBooking.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                    selectedBooking.status === 'checked_in' ? 'bg-blue-100 text-blue-700' : ''
                  }>
                    {selectedBooking.status === 'checked_in' ? 'Checked In' :
                     selectedBooking.status === 'in_progress' ? 'In Progress' :
                     selectedBooking.status === 'no_show' ? 'No Show' :
                     selectedBooking.status.charAt(0).toUpperCase() + selectedBooking.status.slice(1)}
                  </Badge>
                </div>
                
                {/* Action buttons based on status - Admin can only cancel/mark no-show, not perform therapy */}
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  {/* Scheduled: Can Mark No-Show, Cancel */}
                  {selectedBooking.status === 'scheduled' && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleMarkNoShow}
                        data-testid="no-show-btn"
                      >
                        <XCircle className="w-4 h-4 mr-1" /> No Show
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleCancelBooking}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid="cancel-booking-btn"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  
                  {/* Checked-In or In Progress: Admin can only cancel */}
                  {(selectedBooking.status === 'checked_in' || selectedBooking.status === 'in_progress') && (
                    <>
                      <div className="w-full p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                        <Play className="w-4 h-4 inline mr-1" />
                        {selectedBooking.status === 'checked_in' ? 'Guest checked in - Waiting for therapist to start therapy' : 'Therapy in progress - Therapist will complete'}
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleCancelBooking}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  
                  {/* Completed: Show completion info */}
                  {selectedBooking.status === 'completed' && (
                    <div className="w-full p-3 bg-green-50 rounded-lg text-sm text-green-700">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Completed {selectedBooking.completed_at ? `on ${new Date(selectedBooking.completed_at).toLocaleString()}` : ''}
                    </div>
                  )}
                  
                  {/* Cancelled/No-Show: Show info */}
                  {(selectedBooking.status === 'cancelled' || selectedBooking.status === 'no_show') && (
                    <div className="w-full p-3 bg-red-50 rounded-lg text-sm text-red-700">
                      {selectedBooking.status === 'cancelled' ? 'Booking was cancelled' : 'Guest did not show up'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={checkoutDialogOpen} onOpenChange={setCheckoutDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Checkout</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedBooking?.booking_type === 'package' ? (
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-green-700 font-medium">Package Prepaid</p>
                <p className="text-sm text-green-600">No additional payment required</p>
              </div>
            ) : (
              <>
                <div>
                  <Label>Payment Status</Label>
                  <Select
                    value={checkoutForm.payment_status}
                    onValueChange={(value) => setCheckoutForm({ ...checkoutForm, payment_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="complimentary">Complimentary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {checkoutForm.payment_status === 'paid' && (
                  <>
                    <div>
                      <Label>Payment Mode</Label>
                      <Select
                        value={checkoutForm.payment_mode}
                        onValueChange={(value) => setCheckoutForm({ ...checkoutForm, payment_mode: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="card">Card</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Payment Received By</Label>
                      {(currentProperty?.ownership_type === 'our_property' || currentProperty?.ownership_type === 'owned_with_rent') ? (
                        <div className="p-3 bg-green-50 rounded-lg text-green-700 text-sm">
                          Company ({currentProperty?.ownership_type === 'owned_with_rent' ? 'Owned with Rent' : 'Fully Owned'})
                        </div>
                      ) : (
                        <Select
                          value={checkoutForm.payment_received_by}
                          onValueChange={(value) => setCheckoutForm({ ...checkoutForm, payment_received_by: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hotel">Hotel</SelectItem>
                            <SelectItem value="nirvaana">Nirvaana</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </>
                )}
                
                {checkoutForm.payment_status === 'complimentary' && (
                  <div className="p-3 bg-amber-50 rounded-lg text-amber-700 text-sm">
                    {user?.role === 'super_admin' 
                      ? 'Complimentary will be approved automatically'
                      : 'Complimentary requires admin approval'}
                  </div>
                )}
              </>
            )}
            
            <Button onClick={handleCheckout} className="w-full">
              Complete Checkout
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Start Maintenance</DialogTitle>
            {maintenanceRoom && (
              <p className="text-sm text-muted-foreground">{maintenanceRoom.room_name}</p>
            )}
          </DialogHeader>
          
          <form onSubmit={submitMaintenance} className="space-y-4">
            <div>
              <Label htmlFor="maintenance_reason">Reason</Label>
              <Input
                id="maintenance_reason"
                value={maintenanceForm.reason}
                onChange={(e) => setMaintenanceForm({ ...maintenanceForm, reason: e.target.value })}
                placeholder="e.g., AC repair, deep cleaning"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="maintenance_end">Expected Completion</Label>
              <Input
                id="maintenance_end"
                type="datetime-local"
                value={maintenanceForm.expected_end_time}
                onChange={(e) => setMaintenanceForm({ ...maintenanceForm, expected_end_time: e.target.value })}
                required
              />
            </div>
            
            <Button type="submit" className="w-full">
              Start Maintenance
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Package Creation Dialog - Multi-Step */}
      <Dialog open={packageDialogOpen} onOpenChange={(open) => {
        setPackageDialogOpen(open);
        if (!open) resetPackageForm();
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Create New Package {packageStep > 1 && `- Step ${packageStep}/3`}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {packageStep === 1 && 'Package Details'}
              {packageStep === 2 && 'Booking & Payment Details'}
              {packageStep === 3 && 'Review & Confirm'}
            </p>
            {/* Progress indicator */}
            <div className="flex gap-2 mt-2">
              {[1, 2, 3].map(step => (
                <div 
                  key={step}
                  className={`h-2 flex-1 rounded-full ${
                    step <= packageStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </DialogHeader>
          
          {/* Step 1: Package Details */}
          {packageStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="package_name">Package Name *</Label>
                <Input
                  id="package_name"
                  value={packageForm.package_name}
                  onChange={(e) => setPackageForm({ ...packageForm, package_name: e.target.value })}
                  placeholder="e.g., 4-Session Relaxation Package"
                  required
                  data-testid="package-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pkg_guest_name">Guest Name *</Label>
                  <Input
                    id="pkg_guest_name"
                    value={packageForm.guest_name}
                    onChange={(e) => setPackageForm({ ...packageForm, guest_name: e.target.value })}
                    required
                    data-testid="pkg-guest-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pkg_guest_gender">Gender *</Label>
                  <Select
                    value={packageForm.guest_gender}
                    onValueChange={(value) => setPackageForm({ ...packageForm, guest_gender: value })}
                  >
                    <SelectTrigger data-testid="pkg-guest-gender-select">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pkg_guest_phone">Guest Phone *</Label>
                  <Input
                    id="pkg_guest_phone"
                    type="tel"
                    value={packageForm.guest_phone}
                    onChange={(e) => setPackageForm({ ...packageForm, guest_phone: e.target.value })}
                    required
                    data-testid="pkg-guest-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pkg_guest_email">Guest Email</Label>
                  <Input
                    id="pkg_guest_email"
                    type="email"
                    value={packageForm.guest_email}
                    onChange={(e) => setPackageForm({ ...packageForm, guest_email: e.target.value })}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="pkg_therapy_type">Therapy Type *</Label>
                <Select
                  value={packageForm.therapy_type}
                  onValueChange={(value) => {
                    const therapy = availableTherapies.find(t => t.name === value);
                    setPackageForm({ 
                      ...packageForm, 
                      therapy_type: value,
                      session_duration: therapy?.duration?.toString() || packageForm.session_duration
                    });
                  }}
                >
                  <SelectTrigger data-testid="pkg-therapy-select">
                    <SelectValue placeholder="Select therapy" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTherapies.map(t => (
                      <SelectItem key={t.name} value={t.name}>
                        {t.name} ({t.duration} min){t.base_price > 0 ? ` - ₹${t.base_price}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="total_sessions">Number of Sessions *</Label>
                  <Input
                    id="total_sessions"
                    type="number"
                    min="1"
                    max="20"
                    value={packageForm.total_sessions}
                    onChange={(e) => setPackageForm({ ...packageForm, total_sessions: e.target.value })}
                    placeholder="e.g., 4"
                    required
                    data-testid="sessions-input"
                  />
                </div>
                <div>
                  <Label htmlFor="session_duration">Session Duration (mins) *</Label>
                  <Select
                    value={packageForm.session_duration}
                    onValueChange={(value) => setPackageForm({ ...packageForm, session_duration: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                      <SelectItem value="90">90 minutes</SelectItem>
                      <SelectItem value="120">120 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="total_price">Total Price (₹) * <span className="text-xs text-muted-foreground">(Excl. GST)</span></Label>
                  <Input
                    id="total_price"
                    type="number"
                    value={packageForm.total_price}
                    onChange={(e) => setPackageForm({ ...packageForm, total_price: e.target.value })}
                    placeholder="e.g., 10000"
                    required
                    data-testid="price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="validity_days">Validity (days)</Label>
                  <Input
                    id="validity_days"
                    type="number"
                    min="30"
                    max="365"
                    value={packageForm.validity_days}
                    onChange={(e) => setPackageForm({ ...packageForm, validity_days: e.target.value })}
                    data-testid="validity-input"
                  />
                </div>
              </div>
              
              {packageForm.total_sessions && packageForm.session_duration && (
                <div className="p-3 bg-muted/30 rounded-lg text-sm">
                  <p>Total Duration: <strong>{parseInt(packageForm.total_sessions) * parseInt(packageForm.session_duration)} minutes</strong> for {packageForm.total_sessions} sessions</p>
                </div>
              )}
              
              <Button 
                type="button" 
                className="w-full"
                onClick={() => {
                  if (!packageForm.package_name || !packageForm.guest_name || !packageForm.guest_gender || 
                      !packageForm.guest_phone || !packageForm.total_sessions || !packageForm.total_price) {
                    toast.error('Please fill all required fields');
                    return;
                  }
                  setPackageStep(2);
                }}
              >
                Next
              </Button>
            </div>
          )}

          {/* Step 2: Booking & Payment Details */}
          {packageStep === 2 && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-medium">Booking Type</Label>
                  <Badge className="bg-purple-100 text-purple-700">Package Session</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Fixed for package bookings</p>
              </div>

              <div>
                <Label htmlFor="pkg_guest_type">Guest Type *</Label>
                <Select
                  value={packageForm.guest_type}
                  onValueChange={(value) => setPackageForm({ ...packageForm, guest_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_house">In-house Guest</SelectItem>
                    <SelectItem value="outside">Outside Guest</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="pkg_therapist">Assign Therapist</Label>
                <Select
                  value={packageForm.therapist_id}
                  onValueChange={(value) => setPackageForm({ ...packageForm, therapist_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="as_per_availability">As per availability</SelectItem>
                    {therapists
                      .filter(t => t.assigned_property_id === selectedProperty)
                      .map(t => (
                        <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="pkg_discount">Discount (%)</Label>
                <Input
                  id="pkg_discount"
                  type="number"
                  min="0"
                  max="100"
                  value={packageForm.discount_percent}
                  onChange={(e) => setPackageForm({ ...packageForm, discount_percent: e.target.value })}
                  placeholder="0"
                />
              </div>

              <div className="p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-medium text-green-700">Payment Type</Label>
                  <Badge className="bg-green-100 text-green-700">Prepaid</Badge>
                </div>
                <p className="text-sm text-green-600 mt-1">Package creation requires upfront payment</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pkg_payment_received_by">Payment Received By *</Label>
                  {(currentProperty?.ownership_type === 'our_property' || currentProperty?.ownership_type === 'owned_with_rent') ? (
                    <div className="p-3 bg-green-50 rounded-lg text-green-700 text-sm">
                      Company ({currentProperty?.ownership_type === 'owned_with_rent' ? 'Owned with Rent' : 'Fully Owned'})
                    </div>
                  ) : (
                    <Select
                      value={packageForm.payment_received_by}
                      onValueChange={(value) => setPackageForm({ ...packageForm, payment_received_by: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hotel">Hotel</SelectItem>
                        <SelectItem value="nirvaana">Nirvaana Wellness</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label htmlFor="pkg_payment_mode">Payment Mode</Label>
                  <Select
                    value={packageForm.payment_mode}
                    onValueChange={(value) => setPackageForm({ ...packageForm, payment_mode: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline"
                  className="flex-1"
                  onClick={() => setPackageStep(1)}
                >
                  Previous
                </Button>
                <Button 
                  type="button" 
                  className="flex-1"
                  onClick={() => {
                    if (currentProperty?.ownership_type !== 'our_property' && currentProperty?.ownership_type !== 'owned_with_rent' && !packageForm.payment_received_by) {
                      toast.error('Please select payment received by');
                      return;
                    }
                    setPackageStep(3);
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Review & Confirm */}
          {packageStep === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <h4 className="font-medium">Package Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Package Name:</span>
                  <span className="font-medium">{packageForm.package_name}</span>
                  
                  <span className="text-muted-foreground">Guest:</span>
                  <span className="font-medium">{packageForm.guest_name} ({packageForm.guest_gender})</span>
                  
                  <span className="text-muted-foreground">Phone:</span>
                  <span className="font-medium">{packageForm.guest_phone}</span>
                  
                  <span className="text-muted-foreground">Therapy:</span>
                  <span className="font-medium">{packageForm.therapy_type}</span>
                  
                  <span className="text-muted-foreground">Sessions:</span>
                  <span className="font-medium">{packageForm.total_sessions} sessions × {packageForm.session_duration} mins</span>
                  
                  <span className="text-muted-foreground">Validity:</span>
                  <span className="font-medium">{packageForm.validity_days} days</span>
                </div>
              </div>

              <div className="p-4 bg-primary/5 rounded-lg space-y-2">
                <h4 className="font-medium">Payment Details</h4>
                <div className="flex justify-between text-sm">
                  <span>Base Price</span>
                  <span>₹{packagePrice.base}</span>
                </div>
                {parseFloat(packagePrice.discountAmount) > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Discount ({packagePrice.discountPercent}%)</span>
                    <span>-₹{packagePrice.discountAmount}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span>GST (5%)</span>
                  <span>₹{packagePrice.gst}</span>
                </div>
                <div className="flex justify-between text-base font-medium border-t pt-2 mt-2">
                  <span>Total Amount</span>
                  <span className="text-primary">₹{packagePrice.total}</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Per Session Cost</span>
                  <span>₹{(parseFloat(packagePrice.total) / parseInt(packageForm.total_sessions)).toFixed(0)}</span>
                </div>
              </div>

              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                <p className="font-medium">Customer can redeem {packageForm.total_sessions} sessions anytime within {packageForm.validity_days} days.</p>
                <p className="mt-1">They can walk-in and book with any therapist.</p>
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline"
                  className="flex-1"
                  onClick={() => setPackageStep(2)}
                >
                  Previous
                </Button>
                <Button 
                  type="button" 
                  className="flex-1"
                  onClick={handleCreatePackage}
                  data-testid="create-package-submit"
                >
                  Create Package
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Booking Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Reschedule Booking</DialogTitle>
            {rescheduleBooking && (
              <p className="text-sm text-muted-foreground">
                {rescheduleBooking.guest_name} - {rescheduleBooking.therapy_type}
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <p><strong>Current:</strong> {rescheduleBooking?.booking_date} at {rescheduleBooking?.start_time}</p>
            </div>
            
            <div>
              <Label htmlFor="new_date">New Date *</Label>
              <Input
                id="new_date"
                type="date"
                value={rescheduleForm.new_date}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, new_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <Label htmlFor="new_time">New Time *</Label>
              <Select
                value={rescheduleForm.new_time}
                onValueChange={(value) => setRescheduleForm({ ...rescheduleForm, new_time: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setRescheduleDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1"
                onClick={handleRescheduleSubmit}
              >
                Reschedule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record New Service Dialog (Therapist Flow) */}
      <Dialog open={recordServiceOpen} onOpenChange={setRecordServiceOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Record New Service</DialogTitle>
            <DialogDescription>
              Select how you want to record this service
            </DialogDescription>
          </DialogHeader>
          
          {serviceFlowStep === 'select' && (
            <div className="space-y-4">
              {/* Pre-booked Customers */}
              {preBookedCustomers.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Today's Pre-Booked Customers ({preBookedCustomers.length})
                  </h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {preBookedCustomers.map(booking => (
                      <button
                        key={booking.id}
                        onClick={() => handleSelectPreBooked(booking)}
                        className="w-full p-3 border rounded-lg text-left hover:border-primary hover:bg-primary/5 transition-all"
                        data-testid={`prebooked-${booking.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{booking.guest_name}</p>
                            <p className="text-sm text-muted-foreground">{booking.therapy_type}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">{booking.start_time}</p>
                            <Badge variant="outline" className="text-xs">
                              {booking.booking_type === 'package' ? 'Package' : 'One-time'}
                            </Badge>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {preBookedCustomers.length === 0 && (
                <div className="text-center py-4 text-muted-foreground">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No pre-booked customers for today</p>
                </div>
              )}
              
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-medium text-sm text-muted-foreground">Or record a walk-in:</h4>
                
                <button
                  onClick={() => {
                    setServiceFlowStep('package_walkin');
                    setBookingType('package');
                  }}
                  className="w-full p-4 border-2 border-dashed rounded-lg text-left hover:border-purple-400 hover:bg-purple-50 transition-all flex items-center gap-3"
                  data-testid="package-walkin-btn"
                >
                  <Package className="w-6 h-6 text-purple-600" />
                  <div>
                    <p className="font-medium">Package Walk-in</p>
                    <p className="text-sm text-muted-foreground">Customer has an existing package</p>
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setServiceFlowStep('new_walkin');
                    setBookingType('one_time');
                    resetBookingForm();
                  }}
                  className="w-full p-4 border-2 border-dashed rounded-lg text-left hover:border-green-400 hover:bg-green-50 transition-all flex items-center gap-3"
                  data-testid="new-walkin-btn"
                >
                  <Users className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium">New Walk-in</p>
                    <p className="text-sm text-muted-foreground">First-time or one-time service</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {serviceFlowStep === 'package_walkin' && (
            <div className="space-y-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setServiceFlowStep('select')}
                className="mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              
              <div className="space-y-3">
                <Label>Search by Phone Number</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter guest phone number"
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                  />
                  <Button type="button" onClick={searchPackagesByPhone}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {foundPackages.length > 0 && (
                <div className="space-y-2">
                  <Label>Active Packages</Label>
                  {foundPackages.map(pkg => (
                    <button
                      key={pkg.id}
                      onClick={() => {
                        selectPackage(pkg);
                        // Find an available room and open booking dialog
                        const availableRoom = rooms.find(r => r.status === 'available');
                        if (availableRoom) {
                          const now = new Date();
                          const currentTime = `${now.getHours().toString().padStart(2, '0')}:${Math.floor(now.getMinutes() / 30) * 30 === 0 ? '00' : '30'}`;
                          setSelectedSlot({ room: availableRoom, time: currentTime });
                          setRecordServiceOpen(false);
                          setBookingDialogOpen(true);
                        } else {
                          toast.error('No available rooms. Please check room status.');
                        }
                      }}
                      className={`w-full p-3 border rounded-lg text-left transition-all hover:border-primary`}
                    >
                      <div className="flex justify-between">
                        <span className="font-medium">{pkg.guest_name}</span>
                        <Badge className="bg-purple-100 text-purple-700">
                          {pkg.sessions_remaining || pkg.remaining_sessions} sessions left
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{pkg.package_name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Therapy: {pkg.therapy_type} • Valid till: {pkg.valid_until}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {serviceFlowStep === 'new_walkin' && (
            <div className="space-y-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setServiceFlowStep('select')}
                className="mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              
              <p className="text-sm text-muted-foreground">
                Select an available slot from the room grid to create a new walk-in booking.
              </p>
              
              <Button 
                onClick={() => setRecordServiceOpen(false)}
                className="w-full"
              >
                View Room Grid
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rate Customer Dialog */}
      <Dialog open={rateCustomerDialogOpen} onOpenChange={setRateCustomerDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Rate Customer
            </DialogTitle>
            {selectedServiceForRating && (
              <p className="text-sm text-muted-foreground">
                {selectedServiceForRating.guest_name} • {selectedServiceForRating.therapy_type}
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-5">
            {/* Overall Rating */}
            <div>
              <Label className="text-sm font-medium">Overall Experience</Label>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRatingForm(prev => ({ ...prev, overall_rating: star }))}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star className={`w-8 h-8 ${ratingForm.overall_rating >= star ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Behavior Rating */}
            <div>
              <Label className="text-sm font-medium">Behavior & Cooperation</Label>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRatingForm(prev => ({ ...prev, behavior_rating: star }))}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star className={`w-6 h-6 ${ratingForm.behavior_rating >= star ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Punctuality Rating */}
            <div>
              <Label className="text-sm font-medium">Punctuality</Label>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRatingForm(prev => ({ ...prev, punctuality_rating: star }))}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star className={`w-6 h-6 ${ratingForm.punctuality_rating >= star ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Hygiene Rating */}
            <div>
              <Label className="text-sm font-medium">Hygiene & Cleanliness</Label>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRatingForm(prev => ({ ...prev, hygiene_rating: star }))}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star className={`w-6 h-6 ${ratingForm.hygiene_rating >= star ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Would Serve Again */}
            <div>
              <Label className="text-sm font-medium">Would you serve this customer again?</Label>
              <RadioGroup
                value={ratingForm.would_serve_again === null ? '' : ratingForm.would_serve_again.toString()}
                onValueChange={(val) => setRatingForm(prev => ({ ...prev, would_serve_again: val === 'true' }))}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="serve-yes" />
                  <Label htmlFor="serve-yes" className="text-green-600 cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="serve-no" />
                  <Label htmlFor="serve-no" className="text-red-600 cursor-pointer">No</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Comment */}
            <div>
              <Label className="text-sm font-medium">Additional Comments (Optional)</Label>
              <Textarea
                value={ratingForm.comment}
                onChange={(e) => setRatingForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Any notes about this customer..."
                className="mt-2"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRateCustomerDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitRating} className="bg-amber-500 hover:bg-amber-600">
              <Star className="w-4 h-4 mr-2" />
              Submit Rating
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperationsDashboard;
